import React, { useState, useContext } from "react"
import Loader from "../../ui/loader"
import moment from "moment"
import { Button } from "../../ui/button"
import { ProfileClientPaymentsItem } from "../../../types/clientPaymentTypes"
import { ServiceRequests } from "../../../api/app.service"
import { SvgDownload, SvgEye } from "../../icons"
import { UserCtx } from "../../../context/userContext"
import { useToast } from "../../../hooks/useToast"

interface Props {
  invoice: ProfileClientPaymentsItem
  serviceState: any
  onRefundSuccess: () => void
  contact: any
}

const BillingPreviewItem: React.FC<Props> = ({
  invoice,
  serviceState,
  onRefundSuccess,
  contact,
}) => {
  const { user } = useContext(UserCtx)
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  const formattedAmount = parseInt(invoice.amount).toLocaleString()

  const formatStripeStatus = (stripeStatus: string) => {
    if (stripeStatus === "succeeded") {
      return "Paid"
    } else {
      return stripeStatus
    }
  }

  const refundService = async () => {
    if (user) {
      setLoading(true)
      try {
        const data = await ServiceRequests.refundService({
          capture_id: invoice.paypal_capture_id,
          amount: invoice.amount,
        })
        toast.success("Refund Successful!")
        setLoading(false)
        onRefundSuccess()
      } catch (error: any) {
        console.error("Refund service error:", error.message)
        toast.error(`Refund failed: ${error.message}`)
        setLoading(false)
      }
    } else {
      console.warn("User is not logged in.")
      toast.error("You must be logged in to process a refund.")
      setLoading(false)
    }
  }

  return (
    <div className="w-full border border-grayMist rounded-[10px] px-2 xl:px-[24px] flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-0 overflow-hidden h-auto sm:h-[76px]">
      <div className="flex flex-col sm:flex-row items-start sm:items-center flex-1 min-w-0 overflow-hidden gap-1 sm:gap-2">
        <span className="flex-shrink-0">
          {moment(invoice?.transaction_date).format("MM/DD/YY")}
        </span>
        <div className="hidden sm:flex w-[1px] h-[40px] bg-grayMist mx-0 sm:mx-2 flex-shrink-0" />
        <div className="hidden sm:flex flex-col flex-1 min-w-0 overflow-hidden">
          <p
            className="font-bold text-4 truncate"
            title={invoice?.service_name}
          >
            {invoice?.service_name}
          </p>
          <span className="text-gray text-base truncate">
            {formatStripeStatus(invoice.stripe_status)}
          </span>
        </div>
        <div className="sm:hidden flex flex-col min-w-0 overflow-hidden">
          <p className="font-bold text-4" title={invoice?.service_name}>
            {invoice?.service_name.length > 25 && window.innerWidth < 640
              ? invoice?.service_name.slice(0, 25) + "…"
              : invoice?.service_name}
          </p>
          <span className="text-gray text-base">
            {formatStripeStatus(invoice.stripe_status)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-4 md:gap-[24px] flex-shrink-0 mb-2 sm:mb-0">
        <h5 className="font-bold text-4 truncate">
          {invoice &&
            String.fromCharCode(parseInt(invoice?.symbol?.slice(2), 16))}
          {formattedAmount}
        </h5>

        {formattedAmount !== "0" &&
        (serviceState === "client" || contact.profile_type === "member") ? (
          <Button
            onClick={refundService}
            className="w-[95px] flex-shrink-0"
            disabled={invoice.payment_status === "REFUNDED"}
          >
            {loading ? (
              <Loader />
            ) : invoice.payment_status === "REFUNDED" ? (
              "Refunded"
            ) : (
              "Refund"
            )}
          </Button>
        ) : (
          <div className="w-[95px] h-[32px] bg-transparent flex-shrink-0" />
        )}
      </div>
    </div>
  )
}

export default BillingPreviewItem

interface DropdownProps {
  viewInvoiceUrl: string
  downloadInvoiceUrl: string
}

const BillingPreviewItemDropdown: React.FC<DropdownProps> = ({
  viewInvoiceUrl,
  downloadInvoiceUrl,
}) => {
  return (
    <div className="flex flex-col bg-white px-4 py-[12px] rounded-[12px] card-hover-shadow">
      <a
        href={viewInvoiceUrl}
        target="_blank"
        className="text-base w-[216px] px-4 py-[12px] flex items-center gap-[8px] rounded-[12px] hover:bg-hover"
        rel="noreferrer"
      >
        <SvgEye />
        View Receipt
      </a>
      <a
        href={downloadInvoiceUrl}
        target="_blank"
        className="text-base w-[216px] px-4 py-[12px] flex items-center gap-[8px] rounded-[12px] hover:bg-hover"
        rel="noreferrer"
      >
        <SvgDownload />
        Download Invoice PDF
      </a>
    </div>
  )
}
